import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { filter, startWith } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

import { AppHeader } from '../app-header/app-header';
import { AppFooter } from '../app-footer/app-footer';
import { SidebarService } from '../../services/sidebar.service';
import { AuthStateService } from '../../shared/states/auth.state';
import { AdminPreviewContentType, AdminPreviewGrant, AdminPreviewService } from '../../shared/services/admin-preview.service';

const PREVIEW_ROUTE: Record<string, AdminPreviewContentType> = { post: 'POST', blog: 'BLOG', event: 'EVENT' };

@Component({
  selector: 'app-app-layout',
  standalone: true,
  imports: [
    CommonModule,
    AppHeader,
    AppFooter,
    RouterModule
  ],
  templateUrl: './app-layout.html'
})
export class AppLayout {
  readonly isExpanded$;
  readonly isHovered$;
  readonly isMobileOpen$;

  showFooter = true; // <-- controls footer visibility

  /** Set while this tab shows an item opened from the admin panel. */
  adminPreview: AdminPreviewGrant | null = null;

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private authState: AuthStateService,
    private adminPreviewService: AdminPreviewService,
  ) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isHovered$ = this.sidebarService.isHovered$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;

    // Hide the footer on /profile, and whenever the user is logged in —
    // re-evaluated on both route changes and auth state changes so logging
    // in/out updates it immediately without needing a navigation.
    combineLatest([
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
      ),
      this.authState.auth$,
    ]).subscribe(([, auth]) => {
      const isProfilePage = this.router.url.includes('/profile');
      this.showFooter = !isProfilePage && !auth?.isLoggedIn;
      this.adminPreview = this.findPreviewForUrl(this.router.url);
    });
  }

  /** Grant for /post|blog|event/:id when this tab was opened as an admin preview. */
  private findPreviewForUrl(url: string): AdminPreviewGrant | null {
    const match = url.split(/[?#]/)[0].match(/^\/(post|blog|event)\/([^/]+)$/);
    if (!match) return null;
    return this.adminPreviewService.findGrant(PREVIEW_ROUTE[match[1]], match[2]);
  }

  get containerClasses() {
    return [
      'flex-1',
      'transition-all',
      'duration-300',
      'ease-in-out',
      (this.isExpanded$ || this.isHovered$) ? 'xl:ml-[290px]' : 'xl:ml-[90px]',
      this.isMobileOpen$ ? 'ml-0' : ''
    ];
  }
}
